import React from 'react'
import classNames from 'classnames'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import { LntCheckbox } from '@app/components/base'
import { Show } from '@app/components/util'

import { isObject } from '@app/typeGuards'
import { TAB_INDEX, KEYS } from '@app/constants'

import { DraggableButton } from '../DraggableButton'

import styles from './styles.module.scss'

import type { MenuItemProps } from './types'
import type { KeyboardEventHandler, CSSProperties } from 'react'

export const MenuItem: React.FC<MenuItemProps> = ({
  index,
  children,
  isSortable,
  checkbox,
  onClick,
  ...dropdownItem
}) => {
  const { id, label, isDisable, isSelect } = dropdownItem

  const { attributes, listeners, isDragging, transform, transition, setNodeRef, setActivatorNodeRef } = useSortable({
    id
  })

  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition
  }

  const className = classNames(styles.dropdownItem, {
    [styles.dropdownItem_dragging]: isDragging,
    [styles.dropdownItem_disable]: isDisable,
    [styles.dropdownItem_select]: isSelect && !checkbox
  })

  const dragButtonClassName = classNames({
    [styles.dropdownItem__draggable_select]: isSelect && !checkbox
  })

  const tabIndex = isDisable ? TAB_INDEX.off : TAB_INDEX.on

  const handleClick = (): void => {
    if (!isDisable) {
      onClick?.({
        ...dropdownItem,
        isSelect: checkbox ? !isSelect : true
      })
    }
  }

  const handleKeyDown: KeyboardEventHandler = (event): void => {
    if (event.key === KEYS.space) {
      handleClick()
    }
  }

  return (
    <li
      tabIndex={ tabIndex }
      className={ className }
      onClick={ handleClick }
      onKeyDown={ handleKeyDown }
      style={ style }
      ref={ setNodeRef }
    >
      <Show condition={ isSortable }>
        <DraggableButton
          className={ dragButtonClassName }
          attributes={ attributes }
          listeners={ listeners }
          setActivatorNodeRef={ setActivatorNodeRef }
        />
      </Show>

      <Show condition={ !!checkbox }>
        <LntCheckbox
          { ...(isObject(checkbox) && checkbox) }
          checked={ isSelect }
          disabled={ isDisable }
          tabIndex={ TAB_INDEX.off }
          className={ styles.dropdownItem__checkbox }
        />
      </Show>

      { label ?? children }
    </li>
  )
}
